import CommonModal from 'components/common/CommonModal';
import DefaultButton from 'components/common/DefaultButton';
import DefaultImageView from 'components/common/DefaultImageView';
import DefaultText from 'components/common/DefaultText';
import {
  getTransferOwnershipCandidates,
  transferContractorOwnership,
  transferEntityOwnership,
  ITransferOwnershipResponse,
} from 'function/api';
import {ITransferOwnershipCandidate} from 'interfaces/interface';
import React, {useEffect, useState} from 'react';
import {useRecoilValue} from 'recoil';
import styled from 'styled-components';
import {colors} from 'styles/colors';
import {userInfoState} from 'utils/Atoms';
import {minmax} from 'utils/minmax';
import {formatPhoneNumber} from 'utils/fomatPhoneNumber';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
  userUUID: string;
  userName: string;
  targetType: 'CONTRACTOR' | 'PARTNER_COMPANY' | 'TEAM' | 'TONG_PLACE';
  targetUUID: string;
  targetName: string;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setErrorModal: React.Dispatch<React.SetStateAction<{}>>;
};

const TARGET_TYPE_LABEL: Record<string, string> = {
  CONTRACTOR: '시공사',
  PARTNER_COMPANY: '협력사',
  TEAM: '팀',
  TONG_PLACE: '현장',
};

const OwnerTransferModal = ({
  isOpen,
  onClose,
  onComplete,
  userUUID,
  userName,
  targetType,
  targetUUID,
  targetName,
  setIsLoading,
  setErrorModal,
}: Props) => {
  const userInfo = useRecoilValue(userInfoState);

  const [step, setStep] = useState<'loading' | 'candidates' | 'confirm' | 'complete' | 'error'>('loading');
  const [candidates, setCandidates] = useState<ITransferOwnershipCandidate[]>([]);
  const [selectedCandidate, setSelectedCandidate] = useState<ITransferOwnershipCandidate | null>(null);
  const [transferResult, setTransferResult] = useState<ITransferOwnershipResponse | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    const loadCandidates = async () => {
      const result = await getTransferOwnershipCandidates({
        userInfo,
        setIsLoading,
        setErrorModal,
        userUUID,
        targetType,
        targetUUID,
      });

      if (result && Array.isArray(result)) {
        if (result.length === 0) {
          setStep('error');
          setErrorMessage('이양 가능한 멤버가 없습니다.');
        } else {
          setCandidates(result);
          setStep('candidates');
        }
      } else {
        setStep('error');
        setErrorMessage('이양 후보 목록을 불러오는데 실패했습니다.');
      }
    };

    loadCandidates();
  }, []);

  const handleTransfer = async () => {
    let result: ITransferOwnershipResponse | false;

    if (targetType === 'CONTRACTOR') {
      result = await transferContractorOwnership({
        userInfo,
        setIsLoading,
        setErrorModal,
        userUUID,
        contractorUUID: targetUUID,
        newOwnerUserUUID: selectedCandidate!.userUUID,
      });
    } else {
      result = await transferEntityOwnership({
        userInfo,
        setIsLoading,
        setErrorModal,
        userUUID,
        newOwnerUserUUID: selectedCandidate!.userUUID,
        targetType,
        targetUUID,
      });
    }

    if (result) {
      setTransferResult(result);
      setStep('complete');
    } else {
      setStep('error');
      setErrorMessage('OWNER 이양 처리 중 오류가 발생했습니다.');
    }
  };

  const renderContent = () => {
    switch (step) {
      case 'loading':
        return (
          <ContentContainer>
            <DefaultText
              text="이양 후보를 조회하는 중..."
              textStyle={{fontSize: minmax(24), textAlign: 'center'}}
            />
          </ContentContainer>
        );

      case 'candidates':
        return (
          <ContentContainer>
            <InfoBox>
              <InfoItem>
                <InfoLabel>이양 대상</InfoLabel>
                <InfoValue>{TARGET_TYPE_LABEL[targetType]} OWNER</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>엔티티</InfoLabel>
                <InfoValue>{targetName}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>현재 OWNER</InfoLabel>
                <InfoValue>{userName}</InfoValue>
              </InfoItem>
            </InfoBox>

            <DefaultText
              text="새 OWNER를 선택하세요:"
              textStyle={{fontSize: minmax(20), marginTop: minmax(20), marginBottom: minmax(10)}}
            />

            <CandidateList>
              {candidates.map((candidate) => {
                const isDisabled = targetType === 'CONTRACTOR' && candidate.isOtherContractorOwner;
                return (
                  <CandidateItem
                    key={candidate.userUUID}
                    $selected={selectedCandidate?.userUUID === candidate.userUUID}
                    $disabled={isDisabled}
                    onClick={() => !isDisabled && setSelectedCandidate(candidate)}
                  >
                    <RadioButton $selected={selectedCandidate?.userUUID === candidate.userUUID} />
                    <CandidateInfo>
                      <CandidateName>{candidate.name}</CandidateName>
                      <CandidatePhone>{formatPhoneNumber({phone: candidate.phone})}</CandidatePhone>
                      <CandidateRole>{candidate.role}</CandidateRole>
                    </CandidateInfo>
                    {isDisabled && (
                      <DisabledBadge>다른 시공사 OWNER</DisabledBadge>
                    )}
                  </CandidateItem>
                );
              })}
            </CandidateList>

            {targetType === 'CONTRACTOR' && (
              <WarningText>
                * 다른 시공사 OWNER인 사용자는 선택할 수 없습니다.
              </WarningText>
            )}

            <ButtonContainer>
              <DefaultButton
                text="취소"
                actived={1}
                buttonStyle={{flex: 1, backgroundColor: colors.lightGrey}}
                textStyle={{fontSize: minmax(20), color: colors.black}}
                onClick={onClose}
              />
              <DefaultButton
                text="다음"
                actived={selectedCandidate ? 1 : 0}
                buttonStyle={{
                  flex: 1,
                  backgroundColor: selectedCandidate ? colors.primary2 : colors.lighterGray,
                }}
                textStyle={{fontSize: minmax(20)}}
                onClick={() => selectedCandidate && setStep('confirm')}
              />
            </ButtonContainer>
          </ContentContainer>
        );

      case 'confirm':
        return (
          <ContentContainer>
            <DefaultText
              text="OWNER 이양을 실행하시겠습니까?"
              textStyle={{fontSize: minmax(26), fontWeight: 'bold', marginBottom: minmax(20)}}
            />
            <InfoBox>
              <InfoItem>
                <InfoLabel>이양 대상</InfoLabel>
                <InfoValue>{TARGET_TYPE_LABEL[targetType]}: {targetName}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>기존 OWNER</InfoLabel>
                <InfoValue>{userName} → COMMON_MEMBER</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>새 OWNER</InfoLabel>
                <InfoValue>{selectedCandidate?.name}</InfoValue>
              </InfoItem>
            </InfoBox>
            <ButtonContainer>
              <DefaultButton
                text="이전"
                actived={1}
                buttonStyle={{flex: 1, backgroundColor: colors.lightGrey}}
                textStyle={{fontSize: minmax(20), color: colors.black}}
                onClick={() => setStep('candidates')}
              />
              <DefaultButton
                text="OWNER 이양 실행"
                actived={1}
                buttonStyle={{flex: 2, backgroundColor: colors.primary2}}
                textStyle={{fontSize: minmax(20)}}
                onClick={handleTransfer}
              />
            </ButtonContainer>
          </ContentContainer>
        );

      case 'complete':
        return (
          <ContentContainer>
            <SuccessIcon>✓</SuccessIcon>
            <DefaultText
              text="OWNER 이양이 완료되었습니다."
              textStyle={{fontSize: minmax(26), fontWeight: 'bold', marginBottom: minmax(20)}}
            />
            <InfoBox>
              <InfoItem>
                <InfoLabel>이양 대상</InfoLabel>
                <InfoValue>{TARGET_TYPE_LABEL[targetType]}: {transferResult?.transferred.targetName}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>새 OWNER</InfoLabel>
                <InfoValue>{transferResult?.transferred.newOwnerName}</InfoValue>
              </InfoItem>
            </InfoBox>
            <DefaultButton
              text="확인"
              actived={1}
              buttonStyle={{width: '100%', backgroundColor: colors.primary2, marginTop: minmax(20)}}
              textStyle={{fontSize: minmax(20)}}
              onClick={onComplete}
            />
          </ContentContainer>
        );

      case 'error':
        return (
          <ContentContainer>
            <ErrorIcon>!</ErrorIcon>
            <DefaultText
              text={errorMessage}
              textStyle={{fontSize: minmax(22), textAlign: 'center', color: colors.vividRed}}
            />
            <DefaultButton
              text="닫기"
              actived={1}
              buttonStyle={{width: '100%', backgroundColor: colors.lightGrey, marginTop: minmax(20)}}
              textStyle={{fontSize: minmax(20), color: colors.black}}
              onClick={onClose}
            />
          </ContentContainer>
        );

      default:
        return null;
    }
  };

  return (
    <CommonModal closeModal={onClose}>
      <ModalContainer>
        <ModalHeader>
          <DefaultText
            text={step === 'complete' ? 'OWNER 이양 완료' : 'OWNER 이양'}
            textStyle={{fontSize: minmax(32), fontWeight: 'bold'}}
          />
          <DefaultImageView
            source="images/common/close.png"
            onClick={onClose}
            style={{
              width: minmax(30),
              height: minmax(30),
              cursor: 'pointer',
            }}
          />
        </ModalHeader>
        {renderContent()}
      </ModalContainer>
    </CommonModal>
  );
};

export default OwnerTransferModal;

// Styled Components
const ModalContainer = styled.div`
  width: 100%;
  max-width: ${minmax(600)};
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${minmax(20)};
  padding-bottom: ${minmax(15)};
  border-bottom: 1px solid ${colors.borderGray};
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const InfoBox = styled.div`
  background-color: ${colors.lighterGray};
  border-radius: ${minmax(8)};
  padding: ${minmax(15)};
`;

const InfoItem = styled.div`
  display: flex;
  justify-content: space-between;
  padding: ${minmax(8)} 0;
  border-bottom: 1px solid ${colors.borderGray};
  &:last-child {
    border-bottom: none;
  }
`;

const InfoLabel = styled.span`
  font-size: ${minmax(18)};
  color: ${colors.darkGray};
`;

const InfoValue = styled.span`
  font-size: ${minmax(18)};
  color: ${colors.black};
  font-weight: 500;
`;

const CandidateList = styled.div`
  max-height: ${minmax(250)};
  overflow-y: auto;
  border: 1px solid ${colors.borderGray};
  border-radius: ${minmax(8)};
`;

const CandidateItem = styled.div<{$selected: boolean; $disabled?: boolean}>`
  display: flex;
  align-items: center;
  padding: ${minmax(12)} ${minmax(15)};
  border-bottom: 1px solid ${colors.borderGray};
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  background-color: ${props => {
    if (props.$disabled) return colors.lighterGray;
    if (props.$selected) return '#e8f0f5';
    return 'white';
  }};
  opacity: ${props => props.$disabled ? 0.6 : 1};
  &:last-child {
    border-bottom: none;
  }
  &:hover {
    background-color: ${props => {
      if (props.$disabled) return colors.lighterGray;
      if (props.$selected) return '#e8f0f5';
      return colors.lighterGray;
    }};
  }
`;

const RadioButton = styled.div<{$selected: boolean}>`
  width: ${minmax(20)};
  height: ${minmax(20)};
  border: 2px solid ${props => props.$selected ? colors.primary : colors.borderGray};
  border-radius: 50%;
  margin-right: ${minmax(12)};
  display: flex;
  align-items: center;
  justify-content: center;
  &::after {
    content: '';
    width: ${minmax(10)};
    height: ${minmax(10)};
    background-color: ${props => props.$selected ? colors.primary : 'transparent'};
    border-radius: 50%;
  }
`;

const CandidateInfo = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  gap: ${minmax(15)};
`;

const CandidateName = styled.span`
  font-size: ${minmax(18)};
  font-weight: 500;
`;

const CandidatePhone = styled.span`
  font-size: ${minmax(16)};
  color: ${colors.darkGray};
`;

const CandidateRole = styled.span`
  font-size: ${minmax(14)};
  color: ${colors.primary};
  background-color: ${'#e8f0f5'};
  padding: ${minmax(2)} ${minmax(8)};
  border-radius: ${minmax(4)};
`;

const DisabledBadge = styled.span`
  font-size: ${minmax(12)};
  color: ${colors.vividRed};
  background-color: #ffebee;
  padding: ${minmax(2)} ${minmax(8)};
  border-radius: ${minmax(4)};
`;

const WarningText = styled.p`
  font-size: ${minmax(14)};
  color: ${colors.darkGray};
  margin-top: ${minmax(10)};
  line-height: 1.5;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: ${minmax(10)};
  margin-top: ${minmax(20)};
`;

const SuccessIcon = styled.div`
  width: ${minmax(60)};
  height: ${minmax(60)};
  background-color: ${colors.primary};
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${minmax(30)};
  margin: 0 auto ${minmax(20)};
`;

const ErrorIcon = styled.div`
  width: ${minmax(60)};
  height: ${minmax(60)};
  background-color: ${colors.vividRed};
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${minmax(30)};
  font-weight: bold;
  margin: 0 auto ${minmax(20)};
`;
